import { Button } from "components/Button/Button";
import { DataTable } from "components/DataTable/DataTable";
import { useLangContext } from "context/Lang";
import { UserCheck, User, XIcon, Search } from "lucide-react";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { routes } from "routes";
import { AsyncProcess } from "services/AsyncProcess";

type AddEditorDialogContentProps = {
    // تم تغيير النوع ليكون رقماً واحداً بدلاً من مصفوفة
    onChangeEditorId: (e: number | null) => void;
    initEditorIds?: number[];
};

export const AddEditorDialogContent = ({
    onChangeEditorId,
    initEditorIds = []
}: AddEditorDialogContentProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [users, setUsers] = useState<UserType[]>([]);

    
    // تغيير الحالة لتخزين معرف واحد فقط (null افتراضياً)
    const [selectedEditorId, setSelectedEditorId] = useState<number | null>(null);
    
    const { gW } = useLangContext();

    const getUsers = () => {
        setLoading(true);

        AsyncProcess({
            type: "get",
            url: routes.api.users,
            setError: () => setLoading(false),
            effectProcess: [(res: any) => {
                if (res?.success !== false) {
                    setUsers(res?.data?.content || res?.data || res);
                }
                setLoading(false);
            }]
        });
    };

    useEffect(() => {
        
        getUsers();
        
    }, []);

    // إرسال المعرف المختار للأب عند التغيير
    useEffect(() => {
        onChangeEditorId(selectedEditorId);
    }, [selectedEditorId]);

    const handleSelectResponsible = (user: UserType) => {
        if (user.id) {
            // استبدال القيمة القديمة بالجديدة مباشرة (اختيار واحد فقط)
            setSelectedEditorId(user.id);
        }
    };

    const handleRemoveResponsible = () => {
        setSelectedEditorId(null);
    };

    const columns: any[] = [
        {
            label: gW("name"),
            name: "name",
            cell: (user: UserType) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs">
                        {user.name?.[0]}{user.surname?.[0]}
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm font-medium">{user.name} {user.surname}</span>
                        <span className="text-xs text-primary-400">{user.userName}</span>
                    </div>
                </div>
            )
        },
        {
            label: gW("username"),
            name: "username"
        },
        {
            label: gW("actions"),
            cell: (user: UserType) => (
                <Button
                    type="button"
                    // الزر معطل إذا كان هذا المستخدم هو المختار حالياً
                    disabled={selectedEditorId === user.id}
                    onClick={() => handleSelectResponsible(user)}
                    className="p-button-outlined p-button-sm flex gap-2 items-center"
                >
                    <UserCheck size={14} />
                    {selectedEditorId === user.id ? gW("selected") : gW("select")}
                </Button>
            )
        }
    ];

    // جلب بيانات المستخدم المختار حالياً لعرضها في البطاقة
    const selectedUser = users.find(u => u.id === selectedEditorId);

    return (
        <div className="min-h-80 flex flex-col gap-4">

            {/* عرض المحرر المختار */}
            <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-primary-700">
                    <User size={16} />
                    {gW("selected-responsible")}
                </h3>
                <div className="flex items-center min-h-[60px] p-3 bg-primary-50 rounded-xl border border-dashed border-primary-300">
                    {!selectedEditorId ? (
                        <span className="text-xs text-primary-400 italic w-full text-center">{gW("no-selection-yet")}</span>
                    ) : (
                        <div className="flex items-center justify-between w-full bg-white border border-primary-200 px-4 py-2 rounded-lg shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">
                                    {selectedUser?.name?.[0]}{selectedUser?.surname?.[0]}
                                </div>
                                <span className="text-sm font-medium">
                                    {selectedUser ? `${selectedUser.name} ${selectedUser.surname}` : `${gW("user")} #${selectedEditorId}`}
                                </span>
                            </div>
                            <button
                                onClick={handleRemoveResponsible}
                                className="text-primary-400 hover:text-red-500 transition-colors p-1"
                            >
                                <XIcon size={18} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* الجدول */}
            <div className="border rounded-xl overflow-hidden bg-white shadow-sm flex-1">
                <DataTable
                    // استبعاد المستخدم المختار حالياً والمستخدمين الموجودين مسبقاً من القائمة
                    data={users.filter(u => u.id && u.id !== selectedEditorId && !initEditorIds.includes(u.id))}
                    columns={columns}
                    loading={loading}
                    rowKey={(user: UserType) => user.id?.toString() || ""}
                    rows={5}
                />
            </div>
        </div>
    );
};